import {
  DynamicLinkInternalTarget,
  DynamicLinkType,
  PortalServiceBaseMenuItemDto,
  PortalServiceMenuItemDto,
  PortalServiceMenuItemDtoPositionType,
} from '@nimbus/portal-service';
import { Permissions } from './permissions';

export interface MenuState {
  ids: string[];
  entities: Record<string, MenuItem>;
  activeItemIds: string[];
  rootOrder: string[];
}

export type MenusState = Record<MenuType, MenuState>;

export enum MenuItemCustomClass {
  ITEM,
  ITEM_SELECTED,
}

export enum MenuItemType {
  HOME = 'home',
  LINK = 'link',
  NOTES_FOLDER = 'notesFolder',
  NOTE = 'note',
  PORTAL_SECTION = 'portalSection',
  PORTAL_PROCESS = 'portalProcess',
  PORTAL_PAGE = 'portalPage',
  UNKNOWN = 'unknown',
  ALL_PAGES = 'allPages',
  TASKS = 'tasks',
  CHAT = 'chat',
  FOLDERS = 'folders',
  FILES = 'portalFilesDashboard',
  FILES_MANAGER = 'filesManager',
  TASKS_DASHBOARD = 'portalTasksDashboard',
  CHATS_DASHBOARD = 'portalChatsDashboard',
  TAGS = 'tags',
  TAG = 'tag',
  AI_AGENTS = 'aiAgents',
}

export type PortalDashboardMenuItemType =
  | MenuItemType.FILES
  | MenuItemType.TASKS_DASHBOARD
  | MenuItemType.CHATS_DASHBOARD;
export const DashboardMenuItemTypes = [MenuItemType.FILES, MenuItemType.TASKS_DASHBOARD, MenuItemType.CHATS_DASHBOARD];

export type FolderMenuItem = NotesFolderMenuItem | PortalSectionMenuItem;
export type SectionMenuItem = FolderMenuItem | PortalProcessMenuItem | TagsMenuItem;
export type PageMenuItem = PortalPageMenuItem | NoteMenuItem;

export const isProcessMenuItemType = (menuItem?: MenuItem): menuItem is PortalProcessMenuItem => {
  if (menuItem == null) {
    return false;
  }
  return menuItem.type === MenuItemType.PORTAL_PROCESS;
};

export const isFolderMenuItemType = (menuItem?: MenuItem): menuItem is FolderMenuItem => {
  if (menuItem == null) {
    return false;
  }
  return menuItem.type === MenuItemType.NOTES_FOLDER || menuItem.type === MenuItemType.PORTAL_SECTION;
};

export const isTagsMenuItemType = (menuItem?: MenuItem): menuItem is TagsMenuItem => {
  if (menuItem == null) {
    return false;
  }
  return menuItem.type === MenuItemType.TAGS;
};

export const isSectionMenuItemType = (menuItem?: MenuItem): menuItem is SectionMenuItem => {
  return isFolderMenuItemType(menuItem) || isProcessMenuItemType(menuItem) || isTagsMenuItemType(menuItem);
};

export const isPageMenuItemType = (menuItem?: MenuItem): menuItem is PageMenuItem => {
  if (menuItem == null) {
    return false;
  }
  return menuItem.type === MenuItemType.PORTAL_PAGE || menuItem.type === MenuItemType.NOTE;
};

export type MenuItem =
  | HomeMenuItem
  | SectionMenuItem
  | PageMenuItem
  | LinkMenuItem
  | AllPagesMenuItem
  | TasksMenuItem
  | ChatMenuItem
  | FoldersMenuItem
  | TagsMenuItem
  | TagMenuItem
  | AIAgentsMenuItem
  | FilesMenuItem
  | FilesManagerMenuItem
  | ChatsDashboardMenuItem
  | TasksDashboardMenuItem;

export interface MenuItemBase {
  id: string;
  type: MenuItemType;
  parentId: string;
  index: number;
  visible: boolean;
  url: string;
  slug: string;
  originalUrl?: string;
  openInNewTab: boolean;
  hasChildren: boolean;
  icon: string;
  iconUrl?: string;
  name: string;
  order?: string[];
  targetId?: string;
  positionType?: string;
  createdAt?: number;
  updatedAt?: number;
  /**
   * whether the item is shared publicly (applicable for internal space pages/folders)
   */
  isShared?: boolean;
  pageId?: string;
  isDraft?: boolean;
  permissions?: Permissions;
  deleted?: boolean;
  isOrigin?: boolean | null;
  originGlobalId?: string | null;
}

export interface HomeMenuItem extends MenuItemBase {
  type: MenuItemType.HOME;
}
export interface NotesFolderMenuItem extends MenuItemBase {
  type: MenuItemType.NOTES_FOLDER;
  targetId: string;
}

export interface FoldersMenuItem extends MenuItemBase {
  type: MenuItemType.FOLDERS;
}

export interface TagsMenuItem extends MenuItemBase {
  type: MenuItemType.TAGS;
}

export interface AIAgentsMenuItem extends MenuItemBase {
  type: MenuItemType.AI_AGENTS;
}

export interface TagMenuItem extends MenuItemBase {
  type: MenuItemType.TAG;
}

export interface NoteMenuItem extends MenuItemBase {
  type: MenuItemType.NOTE;
  targetId: string;
  positionType?: string;
}

export interface LinkMenuItem extends MenuItemBase {
  type: MenuItemType.LINK;
  openInNewTab: boolean;
}

export interface PortalPageMenuItem extends MenuItemBase {
  type: MenuItemType.PORTAL_PAGE;
}

export interface PortalSectionMenuItem extends MenuItemBase {
  type: MenuItemType.PORTAL_SECTION;
}

export type PortalProcessItemStatus = 'todo' | 'progress' | 'done';
export interface PortalProcessItem {
  id: string;
  visible: boolean;
  status: PortalProcessItemStatus;
}

export type PortalProcessDataStatus = 'todo' | 'progress' | 'done';
export interface PortalProcessData {
  status: PortalProcessDataStatus;
  itemsDone: number;
  itemsCount: number;
  sequential: boolean;
  items: PortalProcessItem[];
}

export interface PortalProcessMenuItem extends MenuItemBase {
  type: MenuItemType.PORTAL_PROCESS;
  process: PortalProcessData;
}

export interface AllPagesMenuItem extends MenuItemBase {
  type: MenuItemType.ALL_PAGES;
}

export interface FilesManagerMenuItem extends MenuItemBase {
  type: MenuItemType.FILES_MANAGER;
}

export interface TasksMenuItem extends MenuItemBase {
  type: MenuItemType.TASKS;
}

export interface ChatMenuItem extends MenuItemBase {
  type: MenuItemType.CHAT;
}

export interface FilesMenuItem extends MenuItemBase {
  type: MenuItemType.FILES;
}

export interface TasksDashboardMenuItem extends MenuItemBase {
  type: MenuItemType.TASKS_DASHBOARD;
}

export interface ChatsDashboardMenuItem extends MenuItemBase {
  type: MenuItemType.CHATS_DASHBOARD;
  name: string;
}

export enum MenuType {
  WEBNOTES_MENU = 'webnotes-menu',
  PORTAL_TOPBAR = 'portal-topbar',
  PORTAL_SIDEBAR = 'portal-sidebar',
  PORTAL_FOOTER = 'portal-footer',
  NIMBUS_CONTENT = 'nimbus-content',
  NODE_PICKER_CONTENT = 'node-picker-content',
}

export function getMenuTypeFromString(type: string): MenuType | null {
  switch (type) {
    case 'webnotes-menu':
      return MenuType.WEBNOTES_MENU;
    case 'portal-topbar':
      return MenuType.PORTAL_TOPBAR;
    case 'portal-sidebar':
      return MenuType.PORTAL_SIDEBAR;
    case 'portal-footer':
      return MenuType.PORTAL_FOOTER;
    case 'nimbus-content':
      return MenuType.NIMBUS_CONTENT;
    default:
      return null;
  }
}

export type PORTAL_MENU_TYPE = MenuType.PORTAL_SIDEBAR | MenuType.PORTAL_TOPBAR | MenuType.PORTAL_FOOTER;
export const PortalMenuTypes: Array<PORTAL_MENU_TYPE> = [
  MenuType.PORTAL_SIDEBAR,
  MenuType.PORTAL_TOPBAR,
  MenuType.PORTAL_FOOTER,
];

export enum MenuTooltipPosition {
  MORE_BUTTON,
  ADD_BUTTON,
}

export type MenuTooltipMap = {
  [key in MenuTooltipPosition]?: {
    [key in MenuItemType]?: string;
  };
};

export type MenuItemAddPayload = { menuType: MenuType; item: MenuItem };
export type MenuItemDeletePayload = MenuItemAddPayload & { soft?: boolean };
export type MenuItemUpdatePayload = { menuType: MenuType; item: MenuItem };
export type MenuItemChangeVisibilityPayload = {
  menuType: MenuType;
  item: Pick<MenuItem, 'id' | 'visible'> & { url?: string };
};
export type MenuItemUpdateSlugPayload = {
  menuType: MenuType;
  item: Pick<MenuItem, 'id' | 'slug' | 'pageId' | 'isOrigin'>;
};
export type MenuItemMovePayload = Pick<MenuItem, 'id' | 'parentId' | 'index'> & {
  menuType: MenuType;
  oldMenuType: MenuType;
};
export type MenuItemChangeTypePayload = {
  menuType: MenuType;
  item:
    | Pick<PortalSectionMenuItem, 'id' | 'type' | 'icon'>
    | Pick<PortalProcessMenuItem, 'id' | 'type' | 'process' | 'icon'>
    | Pick<NotesFolderMenuItem, 'id' | 'type' | 'targetId' | 'icon'>
    | Pick<TagsMenuItem, 'id' | 'type' | 'icon'>;
};

const menuItemToDtoBase = ({
  id,
  parentId,
  index,
  visible,
  url,
  slug,
  icon,
  name,
  pageId,
  hasChildren,
  openInNewTab,
  updatedAt = 0,
  originGlobalId,
  isOrigin,
}: MenuItem): Omit<PortalServiceBaseMenuItemDto, 'type' | 'positionType'> => ({
  id,
  parentId,
  index,
  visible,
  url,
  slug,
  icon,
  name,
  pageId,
  updatedAt,
  hasChildren,
  openInNewTab,
  originGlobalId,
  isOrigin,
});

const menuItemToDtoFnsMap: Partial<
  Record<
    Exclude<MenuItemType, MenuItemType.UNKNOWN>,
    (model: MenuItem, positionType: PortalServiceMenuItemDtoPositionType) => PortalServiceMenuItemDto
  >
> = {
  [MenuItemType.HOME]: (model, positionType) => {
    model = <HomeMenuItem>model;
    return {
      ...menuItemToDtoBase(model),
      type: model.type,
      positionType,
    };
  },
  [MenuItemType.NOTES_FOLDER]: (model, positionType) => {
    model = <NotesFolderMenuItem>model;
    return {
      ...menuItemToDtoBase(model),
      type: model.type,
      positionType,
      targetId: model.targetId,
    };
  },
  [MenuItemType.FOLDERS]: (model, positionType) => {
    model = <NotesFolderMenuItem>model;
    return {
      ...menuItemToDtoBase(model),
      type: model.type,
      positionType,
      targetId: model.targetId,
    };
  },
  [MenuItemType.NOTE]: (model, positionType) => {
    model = <NoteMenuItem>model;
    return {
      ...menuItemToDtoBase(model),
      type: model.type,
      positionType,
      targetId: model.targetId,
    };
  },
  [MenuItemType.LINK]: (model, positionType) => {
    model = <LinkMenuItem>model;
    return {
      ...menuItemToDtoBase(model),
      type: model.type,
      positionType,
      dynamicLink: {
        // TODO AndreyQD
        target: DynamicLinkInternalTarget.PAGE,
        targetId: '',
        title: '',
        url: '',
        type: DynamicLinkType.INTERNAL,
      },
    };
  },
  [MenuItemType.PORTAL_PAGE]: (model, positionType) => {
    model = <PortalPageMenuItem>model;
    return {
      ...menuItemToDtoBase(model),
      type: model.type,
      positionType,
      name: model.name,
      targetId: model.targetId,
    };
  },
  [MenuItemType.PORTAL_SECTION]: (model, positionType) => {
    model = <PortalSectionMenuItem>model;
    return {
      ...menuItemToDtoBase(model),
      type: model.type,
      positionType,
      name: model.name,
    };
  },
  [MenuItemType.PORTAL_PROCESS]: (model, positionType) => {
    model = <PortalProcessMenuItem>model;
    return {
      ...menuItemToDtoBase(model),
      type: model.type,
      positionType,
      name: model.name,
      process: model.process,
    };
  },
  [MenuItemType.ALL_PAGES]: (model, positionType) => {
    model = <AllPagesMenuItem>model;
    return {
      ...menuItemToDtoBase(model),
      type: model.type,
      positionType,
    };
  },
  [MenuItemType.FILES_MANAGER]: (model, positionType) => {
    model = <FilesManagerMenuItem>model;
    return {
      ...menuItemToDtoBase(model),
      type: model.type,
      positionType,
      name: model.name,
    };
  },
  [MenuItemType.TASKS]: (model, positionType) => {
    model = <TasksMenuItem>model;
    return {
      ...menuItemToDtoBase(model),
      type: model.type,
      positionType,
    };
  },
  [MenuItemType.CHAT]: (model, positionType) => {
    model = <ChatMenuItem>model;
    return {
      ...menuItemToDtoBase(model),
      type: model.type,
      positionType,
    };
  },
  [MenuItemType.FILES]: (model, positionType) => {
    model = <FilesMenuItem>model;
    return {
      ...menuItemToDtoBase(model),
      type: model.type,
      positionType,
      name: model.name,
    };
  },
  [MenuItemType.CHATS_DASHBOARD]: (model, positionType) => {
    model = <ChatsDashboardMenuItem>model;
    return {
      ...menuItemToDtoBase(model),
      type: model.type,
      positionType,
      name: model.name,
    };
  },
  [MenuItemType.TASKS_DASHBOARD]: (model, positionType) => {
    model = <FilesMenuItem>model;
    return {
      ...menuItemToDtoBase(model),
      type: model.type,
      positionType,
      name: model.name,
    };
  },
};

export function menuItemToUnknownMenuItemDto(model: MenuItem): PortalServiceMenuItemDto {
  return {
    ...menuItemToDtoBase(model),
    type: 'unknown',
    positionType: 'sidebar',
    originalAttributes: model,
  };
}

export function menuItemToDto(
  model: MenuItem,
  positionType: PortalServiceMenuItemDtoPositionType
): PortalServiceMenuItemDto {
  const mapper = menuItemToDtoFnsMap[model.type];
  if (mapper == null) {
    return menuItemToUnknownMenuItemDto(model);
  }
  return mapper(model, positionType);
}

export function isDashboardMenuType(type: MenuItemType) {
  return DashboardMenuItemTypes.includes(type);
}

export interface FetchSidebarMenuTreeParams {
  sessionId: string;
  workspaceId: string;
  parentKey: string | string[];
  abortController?: AbortController;
  force?: boolean;
}
